import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  CheckLg,
  Person,
  Building,
  FileEarmark,
  XCircle,
} from "react-bootstrap-icons";
import "./styles/edit-details.css";
import { useAuth } from "../../contexts/AuthContext";

export default function EditDetails() {
  const { auth } = useAuth();
  const token = auth?.token;

  const USER_API = process.env.REACT_APP_USER_SERVICE_URL;
  const [activeTab, setActiveTab] = useState("personal");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState({
    // personal
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    country: "",

    // business
    storeName: "",
    storeDescription: "",
    companyName: "",
    tinNumber: "",
    businessEmail: "",
    businessPhone: "",

    // address (flattened for UI simplicity)
    street: "",
    businessCity: "",
    businessCountry: "",

    // files
    avatarFile: null,
    avatarPreview: null,

    businessLicenseFile: null,
    tinCertificateFile: null,
    companyLogoFile: null,
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setStatus({ type: "", message: "" });

    (async () => {
      try {
        // 1. PERSONAL PROFILE
        const res = await fetch(`${USER_API}/sellers/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const text = await res.text();

        if (text.trim().startsWith("<!DOCTYPE")) {
          throw new Error("Backend not reachable or proxy misconfigured");
        }

        const data = text ? JSON.parse(text) : {};

        if (!res.ok) {
          throw new Error(data.message || "Failed to load profile");
        }

        // 2. BUSINESS PROFILE
        const businessRes = await fetch(`${USER_API}/sellers/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const businessText = await businessRes.text();

        if (businessText.trim().startsWith("<!DOCTYPE")) {
          throw new Error("Business API returned HTML");
        }

        const businessData = businessText ? JSON.parse(businessText) : {};

        if (!businessRes.ok) {
          throw new Error(
            businessData.message || "Failed to load business profile",
          );
        }

        if (!mounted) return;

        //  NORMALIZE DATA
        const normalized = {
          // personal
          firstName: data.firstName ?? "",
          lastName: data.lastName ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          city: data.city ?? "",
          country: data.country ?? "",
          avatarUrl: data.avatarUrl ?? null,

          // business
          storeName: businessData.storeName ?? "",
          storeDescription: businessData.storeDescription ?? "",
          companyName: businessData.companyName ?? "",
          tinNumber: businessData.tinNumber ?? "",
          businessEmail: businessData.businessEmail ?? "",
          businessPhone: businessData.businessPhone ?? "",

          street: businessData.businessAddress?.street ?? "",
          businessCity: businessData.businessAddress?.city ?? "",
          businessCountry: businessData.businessAddress?.country ?? "",
        };

        setInitial(normalized);

        setForm({
          ...normalized,
          avatarFile: null,
          avatarPreview: normalized.avatarUrl,

          businessLicenseFile: null,
          tinCertificateFile: null,
          companyLogoFile: null,
        });
      } catch (err) {
        console.error(err);
        setStatus({ type: "error", message: err.message });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // validation
  const validators = {
    firstName: (v) =>
      v.trim().length >= 3 || "First name must be at least 3 characters",
    lastName: (v) =>
      v.trim().length >= 3 || "Last name must be at least 3 characters",
    email: (v) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Enter a valid email address",
    phone: (v) => v.trim().length >= 10 || "Enter a valid phone number",
  };

  const [errors, setErrors] = useState({});

  const validateField = useCallback((name, value) => {
    const rule = validators[name];
    if (!rule) return null;
    const res = rule(value);
    return res === true ? null : res;
  }, []);

  const validateAll = useCallback(() => {
    const next = {};
    Object.keys(validators).forEach((k) => {
      const err = validateField(k, form[k]);
      if (err) next[k] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form, validateField]);

  // detect changes

  const hasChanges = (() => {
    if (!initial) return false;

    return (
      form.firstName !== initial.firstName ||
      form.lastName !== initial.lastName ||
      form.email !== initial.email ||
      form.phone !== initial.phone ||
      form.city !== initial.city ||
      form.country !== initial.country ||
      // ADD BUSINESS FIELDS
      form.storeName !== initial.storeName ||
      form.storeDescription !== initial.storeDescription ||
      form.companyName !== initial.companyName ||
      form.tinNumber !== initial.tinNumber ||
      form.businessEmail !== initial.businessEmail ||
      form.businessPhone !== initial.businessPhone ||
      // ADDRESS
      form.street !== initial.street ||
      form.businessCity !== initial.businessCity ||
      form.businessCountry !== initial.businessCountry ||
      // FILES
      form.avatarFile ||
      form.businessLicenseFile ||
      form.tinCertificateFile ||
      form.companyLogoFile
    );
  })();

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
    setErrors((e) => ({ ...e, [name]: null }));
    setStatus({ type: "", message: "" });
  };

  const onPickAvatar = (file) => {
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) {
      setStatus({
        type: "error",
        message: "Only PNG, JPG or WEBP images allowed.",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((s) => ({
        ...s,
        avatarFile: file,
        avatarPreview: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const onAvatarClick = () => fileInputRef.current?.click();

  const onRemoveAvatar = () => {
    setForm((s) => ({ ...s, avatarFile: null, avatarPreview: null }));
  };

  const onCancel = () => {
    if (!hasChanges) return navigate("/dashboard/settings");

    setForm({
      ...initial,
      avatarFile: null,
      avatarPreview: initial.avatarUrl || null,
      businessLicenseFile: null,
      tinCertificateFile: null,
      companyLogoFile: null,
    });

    setErrors({});
    setStatus({ type: "", message: "" });
  };

  const onSave = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!validateAll()) {
      return setStatus({
        type: "error",
        message: "Fix validation errors before saving.",
      });
    }

    if (!hasChanges) {
      return setStatus({ type: "info", message: "No changes to save." });
    }

    setSaving(true);

    try {
      // 1. UPDATE PERSONAL PROFILE
      const personalRes = await fetch(`${USER_API}/sellers/me`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          city: form.city,
          country: form.country, //  FIXED (you had form.city before)
        }),
      });

      if (!personalRes.ok) {
        throw new Error("Failed to update personal profile");
      }

      // 2. UPDATE BUSINESS PROFILE
      const businessRes = await fetch(`${USER_API}/sellers/profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          companyName: form.companyName,
          tinNumber: form.tinNumber,
          businessEmail: form.businessEmail,
          businessPhone: form.businessPhone,
          storeName: form.storeName,
          storeDescription: form.storeDescription,

          businessAddress: {
            street: form.street,
            city: form.businessCity,
            country: form.businessCountry,
          },
        }),
      });

      if (!businessRes.ok) {
        throw new Error("Failed to update business profile");
      }

      // 3. UPLOAD FILES (ONLY IF ANY EXIST)
      if (
        form.businessLicenseFile ||
        form.tinCertificateFile ||
        form.companyLogoFile ||
        form.avatarFile
      ) {
        const fd = new FormData();

        if (form.businessLicenseFile)
          fd.append("businessLicense", form.businessLicenseFile);

        if (form.tinCertificateFile)
          fd.append("tinCertificate", form.tinCertificateFile);

        if (form.companyLogoFile)
          fd.append("companyLogo", form.companyLogoFile);

        // avatar handled separately (your existing endpoint)
        if (form.avatarFile) {
          await uploadAvatar();
        }

        if (
          fd.has("businessLicense") ||
          fd.has("tinCertificate") ||
          fd.has("companyLogo")
        ) {
          const fileRes = await fetch(`${USER_API}/sellers/profile/files`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: fd,
          });

          if (!fileRes.ok) {
            throw new Error("File upload failed");
          }
        }
      }

      setStatus({
        type: "success",
        message: "Profile updated successfully.",
      });

      // OPTIONAL: refresh state
      setInitial((prev) => ({
        ...prev,
        ...form,
      }));
    } catch (err) {
      console.error(err);
      setStatus({
        type: "error",
        message: err.message || "Update failed",
      });
    } finally {
      setSaving(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const uploadAvatar = async () => {
    if (!form.avatarFile) return;

    const formData = new FormData();
    formData.append("file", form.avatarFile);

    const res = await fetch(`${USER_API}/sellers/me/avatar`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!res.ok) {
      throw new Error("Avatar upload failed");
    }

    const data = await res.json();
    return data.avatarUrl;
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;

    if (hasChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to switch tabs?",
      );
      if (!confirmLeave) return;
    }

    setActiveTab(tab);
  };

  return (
    <div className="edit-details">
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h4 className="mb-1">Edit Profile</h4>
          <div className="text-muted small">
            Update your personal information
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate("/dashboard/settings")}
            aria-label="Back to settings"
          >
            Cancel
          </button>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => handleTabChange("personal")}
          >
            <Person size={19} />
            Personal
          </button>
        </li>

        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "business" ? "active" : ""}`}
            onClick={() => handleTabChange("business")}
          >
            <Building size={16} />
            Business
          </button>
        </li>

        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "documents" ? "active" : ""}`}
            onClick={() => handleTabChange("documents")}
          >
            <FileEarmark size={16} />
            Documents
          </button>
        </li>
      </ul>

      <form className="card shadow-sm p-3 p-md-4" onSubmit={onSave} noValidate>
        {status.message && (
          <div
            role={status.type === "error" ? "alert" : "status"}
            className={
              status.type === "success"
                ? "alert alert-success"
                : status.type === "error"
                  ? "alert alert-danger"
                  : "alert alert-info"
            }
          >
            {status.message}
          </div>
        )}

        {activeTab === "personal" && (
          <>
            <div className="row gx-4">
              <aside className="col-md-4 col-lg-3 text-center mb-3 mb-md-0">
                <div className="avatar-edit">
                  <div
                    className="avatar-preview rounded-circle"
                    role="button"
                    tabIndex={0}
                    onClick={onAvatarClick}
                    onKeyDown={(e) => e.key === "Enter" && onAvatarClick()}
                    aria-label="Upload avatar"
                    title="Upload avatar"
                  >
                    {form.avatarPreview ? (
                      <img
                        src={form.avatarPreview}
                        alt={`${form.firstName} ${form.lastName}`}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        <Camera />
                      </div>
                    )}
                  </div>

                  <div className="mt-2 d-flex gap-2 justify-content-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="d-none"
                      onChange={(e) => onPickAvatar(e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={onAvatarClick}
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={onRemoveAvatar}
                      disabled={!form.avatarPreview}
                      aria-disabled={!form.avatarPreview}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="small text-muted mt-2">
                    PNG, JPG or WEBP · max 2MB
                  </div>
                </div>
              </aside>

              <main className="col-md-8 col-lg-9">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">First name</label>
                    <input
                      name="firstName"
                      className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                      value={form.firstName}
                      onChange={onChange}
                      aria-invalid={!!errors.firstName}
                      required
                    />
                    {errors.firstName && (
                      <div className="invalid-feedback">{errors.firstName}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Last name</label>
                    <input
                      name="lastName"
                      className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                      value={form.lastName}
                      onChange={onChange}
                      aria-invalid={!!errors.lastName}
                      required
                    />
                    {errors.lastName && (
                      <div className="invalid-feedback">{errors.lastName}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      name="email"
                      type="email"
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                      value={form.email}
                      onChange={onChange}
                      aria-invalid={!!errors.email}
                      required
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input
                      name="phone"
                      className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                      value={form.phone}
                      onChange={onChange}
                      aria-invalid={!!errors.phone}
                    />
                    {errors.phone && (
                      <div className="invalid-feedback">{errors.phone}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">City</label>
                    <input
                      name="city"
                      className="form-control"
                      value={form.city}
                      onChange={onChange}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Country</label>
                    <input
                      name="country"
                      className="form-control"
                      value={form.country}
                      onChange={onChange}
                    />
                  </div>
                </div>
              </main>
            </div>
          </>
        )}

        <hr className="my-4" />
        {activeTab === "business" && (
          <>
            <h6 className="fw-semibold mb-3">Business Information</h6>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Store Name</label>
                <input
                  name="storeName"
                  className="form-control"
                  value={form.storeName}
                  onChange={onChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Company Name</label>
                <input
                  name="companyName"
                  className="form-control"
                  value={form.companyName}
                  onChange={onChange}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Store Description</label>
                <textarea
                  name="storeDescription"
                  className="form-control"
                  rows={3}
                  value={form.storeDescription}
                  onChange={onChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">TIN Number</label>
                <input
                  name="tinNumber"
                  className="form-control"
                  value={form.tinNumber}
                  onChange={onChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Business Phone</label>
                <input
                  name="businessPhone"
                  className="form-control"
                  value={form.businessPhone}
                  onChange={onChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Business Email</label>
                <input
                  name="businessEmail"
                  className="form-control"
                  value={form.businessEmail}
                  onChange={onChange}
                />
              </div>
            </div>
            <hr className="my-4" />

            <h6 className="fw-semibold mb-3">Business Address</h6>

            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Street Address</label>
                <input
                  name="street"
                  className="form-control"
                  placeholder="e.g. Bole Road, Building 12"
                  value={form.street}
                  onChange={onChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">City</label>
                <input
                  name="businessCity"
                  className="form-control"
                  value={form.businessCity}
                  onChange={onChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Country</label>
                <input
                  name="businessCountry"
                  className="form-control"
                  value={form.businessCountry}
                  onChange={onChange}
                />
              </div>
            </div>
          </>
        )}

        {activeTab === "documents" && (
          <>
            <h6 className="fw-semibold mb-3">Business Documents</h6>

            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Business License</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      businessLicenseFile: e.target.files[0],
                    }))
                  }
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">TIN Certificate</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      tinCertificateFile: e.target.files[0],
                    }))
                  }
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Company Logo</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      companyLogoFile: e.target.files[0],
                    }))
                  }
                />
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 mt-4">
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={!hasChanges || saving}
                aria-disabled={!hasChanges || saving}
                onClick={() => {
                  /* defensive: validate before submit */
                  validateAll();
                }}
                style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }}
              >
                {saving ? "Saving…" : "Save changes"}
                {status.type === "success" && <CheckLg />}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onCancel}
                disabled={saving}
              >
                Discard
              </button>

              <div className="ms-auto small text-muted">
                {hasChanges ? "Unsaved changes" : "All changes saved"}
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
